import React from "react";
import { Link } from "react-router-dom";
import "./LandingDisplay.css";
import { HDMI_PRODUCTS, HDMI_FAQ } from "./HdmiDisplayPage";

/**
 * /hdmi-display/jetson-windows-ubuntu-setup -- how-to guide for connecting the
 * BIBA HDMI touch displays to an NVIDIA Jetson, a Windows PC or an Ubuntu /
 * Debian machine. Static, prerendered (prerender.js STATIC_ROUTES; <head> tags
 * and HowTo/FAQ schema in src/seo/pageMeta.js).
 *
 * Every statement comes from the /hdmi-display landing page (HDMI_FAQ, the
 * "Works with" tiles, spec rows). Do not add facts that are not on that page.
 */

export const JW_STEPS = [
  {
    name: "Connect the HDMI cable",
    text: "Plug an HDMI cable from the Jetson, PC or mini PC into the display. The display reports its native 1024 x 600 mode over EDID.",
  },
  {
    name: "Connect the USB cable",
    text: "Plug the USB cable from the display into a USB port on the host. This one cable carries 5 V power and the capacitive touch data, and the touch controller shows up as a standard USB HID device.",
  },
  {
    name: "Power on and check the display settings",
    text: "Windows 10 and 11, Ubuntu, Debian and JetPack detect the panel and the touch screen with no driver. Use the operating system's display settings to extend or duplicate the desktop and to rotate the screen.",
  },
];

// Per-host notes. Each line is a fact from the landing page.
export const HOSTS = [
  {
    id: "jetson",
    title: "NVIDIA Jetson (Orin Nano, Orin NX, Xavier)",
    points: [
      "HDMI out of the developer kit goes to the display; touch is read over USB and power is 5 V from the carrier.",
      "JetPack and Ubuntu use the kernel's generic HID multitouch driver, so touch works out of the box on Jetson Orin Nano, Orin NX and Xavier developer kits.",
    ],
  },
  {
    id: "windows",
    title: "Windows 10 and 11 (PCs, laptops, mini PCs and NUCs)",
    points: [
      "Windows detects the panel and the touch screen with no driver.",
      "Windows treats the display as a 1024 x 600 monitor and a touch digitiser, so you can use it as a second screen.",
      "Open Display settings to extend or duplicate the desktop, and Display orientation to rotate it.",
    ],
  },
  {
    id: "ubuntu",
    title: "Ubuntu and Debian desktops",
    points: [
      "The touch controller is a standard USB HID device, so touch works without installing anything.",
      "On Ubuntu use Settings, Displays to rotate the screen.",
    ],
  },
];

const GUIDE_FAQ_QUESTIONS = [
  "Can I use it with a Windows laptop or mini PC as a second screen?",
  "Does the touch screen work with NVIDIA Jetson and Ubuntu?",
  "Do I need a driver for the touch screen?",
  "How is the display powered?",
  "How do I rotate the screen to portrait?",
];
export const JW_GUIDE_FAQ = HDMI_FAQ.filter((f) => GUIDE_FAQ_QUESTIONS.includes(f.q));

export default function HdmiJetsonWindowsGuidePage() {
  return (
    <div className="ld-root">
      <section className="ld-hero">
        <div className="ld-wrap ld-hero-grid">
          <div>
            <span className="ld-eyebrow">Setup guide</span>
            <h1 className="ld-h1">
              How to connect an <em>HDMI touch display</em> to an NVIDIA Jetson, a Windows PC or Ubuntu
            </h1>
            <p className="ld-lead">
              One HDMI cable for video and one USB cable for touch and power. No drivers on Windows 10 and 11,
              Ubuntu, Debian or JetPack. This guide covers each host, power and screen rotation.
            </p>
            <div className="ld-cta-row">
              <Link to={HDMI_PRODUCTS[1].route} className="ld-btn ld-btn-primary">
                Buy the 10.1 inch display
              </Link>
              <Link to={HDMI_PRODUCTS[0].route} className="ld-btn ld-btn-secondary">
                Buy the 7 inch display
              </Link>
            </div>
          </div>
          <div className="ld-hero-media">
            <img
              src="/landing/compat/jetson.jpg"
              alt="BIBA 7 inch HDMI touch display connected to a Jetson class AI developer kit with HDMI and USB cables"
              width="800"
              height="600"
              fetchPriority="high"
            />
            <p className="ld-hero-caption">The 7 inch display wired to a Jetson developer kit with one HDMI and one USB cable</p>
          </div>
        </div>
      </section>

      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Setup in three steps</h2>
            <p className="ld-sub">The same steps apply to every host below.</p>
          </div>
          <div className="ld-steps">
            {JW_STEPS.map((s, i) => (
              <div className="ld-step" key={s.name} id={`step-${i + 1}`}>
                <span className="ld-step-num">{i + 1}</span>
                <h3>{s.name}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Notes for each host</h2>
          </div>
          <div className="ld-features">
            {HOSTS.map((h) => (
              <div className="ld-feature" key={h.id} id={h.id}>
                <h3>{h.title}</h3>
                <ul>
                  {h.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="ld-crosslink">
            Using a Raspberry Pi? See the <Link to="/hdmi-display/raspberry-pi-setup">Raspberry Pi setup guide</Link>{" "}
            for the cable that fits each Pi model.
          </div>
        </div>
      </section>

      <section className="ld-section ld-section-alt">
        <div className="ld-wrap ld-two-col">
          <div className="ld-prose">
            <h2 className="ld-h2">Power and touch</h2>
            <p>
              The display is powered at 5 V through its USB connector. For the 10.1 inch model we recommend a
              dedicated 5 V, 2 A supply so the backlight does not share the host's USB budget.
            </p>
            <p>
              The capacitive touch controller is a standard USB HID device. It works on Windows 10 and 11, Ubuntu,
              Debian, Raspberry Pi OS and Android without installing anything.
            </p>
          </div>
          <aside className="ld-aside">
            <h3>At a glance</h3>
            <ul>
              <li>Resolution: 1024 x 600 pixels</li>
              <li>Video: HDMI, EDID auto-detect</li>
              <li>Touch: capacitive, USB HID</li>
              <li>Power: 5 V over USB</li>
              <li>Brightness: 500 nits</li>
              <li>Operating range: -20 to 70 C</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Jetson, Windows and Ubuntu questions</h2>
          </div>
          <div className="ld-faq">
            {JW_GUIDE_FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
          <div className="ld-crosslink">
            See both sizes, price breaks and datasheets on the <Link to="/hdmi-display">HDMI display page</Link>.
            Driving the screen from a microcontroller such as an Arduino, ESP32 or STM32? See the{" "}
            <Link to="/uart-display">Smart UART displays</Link>.
          </div>
        </div>
      </section>

      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-cta">
            <div>
              <h2>Order online or talk to an engineer</h2>
              <p>
                Single units ship from stock in Gurugram. For 100 pieces and above, or a custom cover glass, cable or
                enclosure, send us your requirement and we reply within one working day.
              </p>
            </div>
            <div className="ld-cta-actions">
              <Link to={HDMI_PRODUCTS[0].route} className="ld-btn ld-btn-primary">Buy 7 inch</Link>
              <Link to={HDMI_PRODUCTS[1].route} className="ld-btn ld-btn-primary">Buy 10.1 inch</Link>
              <Link to="/contact" className="ld-btn ld-btn-secondary">Request a quote</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
